import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import Drawer from '../../components/Drawer';
import { ArrowLeft, Edit3, UserCheck, UserX, Briefcase, Wallet, DollarSign, Calendar, Phone, Mail } from 'lucide-react';

function StatusPill({ status }) {
  const cls = status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{status || 'active'}</span>;
}

/* ── Edit Employee Drawer ── */
function EditEmployeeDrawer({ open, onClose, employee, token, onSuccess, currencySymbol }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && employee) {
      setForm({ name: employee.name || '', role: employee.role || '', phone: employee.phone || '', email: employee.email || '', join_date: employee.join_date || '', salary_amount: String(employee.salary_amount || ''), notes: employee.notes || '' });
    }
  }, [open, employee]);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (body.salary_amount) body.salary_amount = parseFloat(body.salary_amount);
      await api.employeePatch(token, employee.id, body);
      toast.show('Employee updated', 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Update failed', 'error'); } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Edit Employee">
      <div className="space-y-4">
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Name</span>
          <input data-testid="edit-emp-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.name || ''} onChange={e => set('name', e.target.value)} /></label>
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Role</span>
          <input data-testid="edit-emp-role" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.role || ''} onChange={e => set('role', e.target.value)} /></label>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone</span>
            <input data-testid="edit-emp-phone" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Email</span>
            <input data-testid="edit-emp-email" type="email" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.email || ''} onChange={e => set('email', e.target.value)} /></label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Join Date</span>
            <input data-testid="edit-emp-join" type="date" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.join_date || ''} onChange={e => set('join_date', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Salary ({currencySymbol})</span>
            <input data-testid="edit-emp-salary" type="number" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200" value={form.salary_amount || ''} onChange={e => set('salary_amount', e.target.value)} /></label>
        </div>
        <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Notes</span>
          <textarea data-testid="edit-emp-notes" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-indigo-200 h-20 resize-none" value={form.notes || ''} onChange={e => set('notes', e.target.value)} /></label>
        <button onClick={submit} disabled={saving} data-testid="edit-emp-submit"
          className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </Drawer>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney, currencySymbol } = useTenantFormat();

  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try { setEmp(await api.employeeGet(token, id)); }
    catch (e) { toast.show(e?.message || 'Load failed', 'error'); }
    finally { setLoading(false); }
  }, [token, id, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    if (!emp) return;
    const newStatus = emp.active_status === 'active' ? 'inactive' : 'active';
    try {
      await api.employeePatch(token, id, { active_status: newStatus });
      toast.show(`Employee ${newStatus === 'active' ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch (e) { toast.show(e?.message || 'Update failed', 'error'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (!emp) return <div className="p-8 text-center text-gray-500">Employee not found</div>;

  const recentPayments = emp.recent_payments || [];

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="employee-detail-page">
      <button onClick={() => navigate('/school/employees')} data-testid="emp-back" className="flex items-center gap-2 text-gray-500 text-sm font-semibold mb-5 hover:text-gray-700"><ArrowLeft size={16} /> Back to Employees</button>

      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-6" data-testid="emp-header">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center"><Briefcase size={28} className="text-indigo-600" /></div>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900">{emp.name}</h1>
              <p className="text-gray-500 text-sm">{emp.role || 'No role specified'}</p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                {emp.phone && <span className="flex items-center gap-1"><Phone size={11} />{emp.phone}</span>}
                {emp.email && <span className="flex items-center gap-1"><Mail size={11} />{emp.email}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={emp.active_status} />
            <button onClick={() => setEditOpen(true)} data-testid="emp-edit-btn" className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50"><Edit3 size={14} /> Edit</button>
            <button onClick={toggleStatus} data-testid="emp-toggle-status"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl border text-sm font-semibold ${emp.active_status === 'active' ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'}`}>
              {emp.active_status === 'active' ? <><UserX size={14} /> Deactivate</> : <><UserCheck size={14} /> Activate</>}
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="emp-kpis">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2"><DollarSign size={16} className="text-indigo-500" /><span className="text-gray-500 text-xs font-bold uppercase">Monthly Salary</span></div>
          <p className="text-2xl font-extrabold text-gray-900 tabular-nums" data-testid="emp-salary">{formatMoney(emp.salary_amount)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2"><Wallet size={16} className="text-emerald-500" /><span className="text-gray-500 text-xs font-bold uppercase">Total Paid YTD</span></div>
          <p className="text-2xl font-extrabold text-gray-900 tabular-nums" data-testid="emp-ytd">{formatMoney(emp.total_salary_paid_ytd || 0)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-2"><Calendar size={16} className="text-amber-500" /><span className="text-gray-500 text-xs font-bold uppercase">Joined</span></div>
          <p className="text-lg font-bold text-gray-900" data-testid="emp-joined">{emp.join_date ? new Date(emp.join_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</p>
        </div>
      </div>

      {/* Pay Salary CTA */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 mb-6 flex items-center justify-between" data-testid="emp-pay-salary-section">
        <div>
          <h3 className="text-indigo-900 font-bold text-sm">Pay Salary</h3>
          <p className="text-indigo-600 text-xs mt-0.5">Create a payment voucher pre-filled for this employee</p>
        </div>
        <button onClick={() => navigate(`/vouchers/payment?employee_id=${id}&employee_name=${encodeURIComponent(emp.name)}&salary=${emp.salary_amount || 0}`)}
          data-testid="emp-pay-salary-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors">
          <Wallet size={16} /> Pay Salary
        </button>
      </div>

      {/* Recent Payments */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="emp-recent-payments">
        <h3 className="text-gray-500 text-xs font-bold uppercase mb-3">Recent Salary Payments</h3>
        {recentPayments.length === 0 ? <p className="text-gray-400 text-sm">No salary payments recorded yet</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Date</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Mode</th>
              <th className="text-right px-4 py-2 text-gray-500 text-xs font-bold uppercase">Amount</th>
              <th className="text-left px-4 py-2 text-gray-500 text-xs font-bold uppercase">Voucher</th>
            </tr></thead>
            <tbody>
              {recentPayments.map((p, i) => (
                <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700">{p.date ? new Date(p.date).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-2 text-gray-600 capitalize">{p.mode || '—'}</td>
                  <td className="px-4 py-2 text-gray-900 text-right tabular-nums font-semibold">{formatMoney(p.amount)}</td>
                  <td className="px-4 py-2">
                    {p.voucher_id ? (
                      <button onClick={() => navigate(`/vouchers/${p.voucher_id}`)} className="text-indigo-600 text-xs font-semibold hover:underline">{p.voucher_number || 'View'}</button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EditEmployeeDrawer open={editOpen} onClose={() => setEditOpen(false)} employee={emp} token={token} onSuccess={() => { setEditOpen(false); load(); }} currencySymbol={currencySymbol} />
    </div>
  );
}
